import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '@/context/AppContext'
import { useToday } from '@/hooks/useToday';
import { useCalendar } from '@/context/CalendarContext';
import type { Schedule } from '@trackmind/core';
import {
  createDefaultPreferences, shiftDateKey, plannedTaskDates, PLANNED_WINDOW_DAYS,
  unscheduledTasks, dormantProjectIds, reschedulePlan, isPausedOn,
  type UnscheduledTaskInfo,
} from '@trackmind/core';

// Re-exported for the components that already import them from here. The
// definitions live in core alongside the ladder that produces them.
export type { UnscheduledReason, UnscheduledTaskInfo } from '@trackmind/core';

export interface UpcomingScheduleData {
  schedules: Schedule[];
  task_schedule_map: Record<string, string>;
  unscheduled_task_ids: string[];
  unscheduled_reasons: Record<string, UnscheduledTaskInfo>;
  total_tasks: number;
  scheduled_count: number;
  unscheduled_count: number;
}

export function useSchedule(date?: string) {
  const { tasks, projects, schedules, loadSchedule, loadSchedules, saveSchedules, preferences, getHabitInstances } = useApp();
  const todayKey = useToday()
  const { busySlotsForDate } = useCalendar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get today's date or specified date
  const targetDate = useMemo(() => {
    return date ?? todayKey();
  }, [date, todayKey]);

  // Get the schedule for the target date
  const schedule = useMemo(() => {
    return schedules.get(targetDate) ?? null;
  }, [schedules, targetDate]);

  // Load schedule on mount or date change
  useEffect(() => {
    loadSchedule(targetDate);
  }, [targetDate, loadSchedule]);

  // Calculate upcoming data
  const upcomingData = useMemo((): UpcomingScheduleData => {
    const today = todayKey();
    const end = shiftDateKey(today, PLANNED_WINDOW_DAYS);

    const scheduleList = Array.from(schedules.values())
      .filter(s => s.date >= today && s.date <= end)
      .sort((a, b) => a.date.localeCompare(b.date));

    // Which projects the scheduler skips — paused, archived or completed,
    // including sub-projects beneath one. Shared with the assistant, which
    // must report the same reason for the same task.
    const dormant = dormantProjectIds(projects);

    const pausedOrBlockedTaskIds = new Set(
      tasks
        .filter(t => isPausedOn(t, todayKey()) || t.is_archived || dormant.has(t.project_id))
        .map(t => t.id)
    );

    // Build task schedule map (excluding paused/blocked tasks) — core's
    // plannedTaskDates, the window the assistant uses too.
    const taskScheduleMap: Record<string, string> = {};
    for (const [taskId, date] of plannedTaskDates(scheduleList, today)) {
      if (!pausedOrBlockedTaskIds.has(taskId)) taskScheduleMap[taskId] = date;
    }

    // Find unscheduled tasks and determine reasons. The ladder — paused,
    // project paused, too short, too long, blocked, no slot — lives in core
    // so the assistant gives the same explanation in the same words.
    const scheduledTaskIds = new Set(Object.keys(taskScheduleMap));
    const unscheduledInfos = unscheduledTasks(scheduledTaskIds, {
      tasks, projects, dormantProjects: dormant, preferences, today: todayKey(),
    });
    const unscheduledTaskIds = unscheduledInfos.map(info => info.taskId);
    const unscheduledReasons: Record<string, UnscheduledTaskInfo> =
      Object.fromEntries(unscheduledInfos.map(info => [info.taskId, info]));

    // Count all non-completed tasks
    const activeTaskCount = tasks.filter(t =>
      t.status !== 'completed'
    ).length;

    return {
      schedules: scheduleList,
      task_schedule_map: taskScheduleMap,
      unscheduled_task_ids: unscheduledTaskIds,
      unscheduled_reasons: unscheduledReasons,
      total_tasks: activeTaskCount,
      scheduled_count: scheduledTaskIds.size,
      unscheduled_count: unscheduledTaskIds.length,
    };
  }, [schedules, tasks, projects, preferences, todayKey]);

  /**
   * The Reschedule button.
   *
   * Every decision — eligible tasks, how many days, the carry-forward that
   * stops a task being placed twice — lives in `reschedulePlan` in core, so
   * the assistant's version of this button is the same code rather than a
   * second implementation that drifts. This hook supplies the data the app
   * can reach and saves the result.
   */
  const reschedule = useCallback(async (options?: { reschedule_all?: boolean; days?: number }) => {
    try {
      setLoading(true);
      setError(null);
      const schedules_ = await reschedulePlan({
        tasks,
        projects,
        preferences: preferences ?? createDefaultPreferences(),
        scheduleFor: (date) => schedules.get(date) ?? null,
        habitInstancesFor: (date) => getHabitInstances(date),
        // Meetings from connected calendars occupy the day before anything of
        // ours is placed. Labelled with the calendar's name, never the event's
        // title — this ends up in the synced Schedule.
        externalBusyFor: (date) => busySlotsForDate(date),
        reschedule_all: options?.reschedule_all,
        days: options?.days,
      });
      await saveSchedules(schedules_);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reschedule');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tasks, projects, preferences, schedules, saveSchedules, getHabitInstances, busySlotsForDate]);

  // Generate schedules function
  const generateSchedules = useCallback(async (days: number = 7, force: boolean = false) => {
    await reschedule({ reschedule_all: force, days });
  }, [reschedule]);

  // Helper to get scheduled date for a task
  const getScheduledDate = useCallback((taskId: string): string | null => {
    return upcomingData.task_schedule_map[taskId] || null;
  }, [upcomingData]);

  return {
    schedule,
    upcomingData,
    taskScheduleMap: upcomingData.task_schedule_map,
    unscheduledTaskIds: upcomingData.unscheduled_task_ids,
    unscheduledReasons: upcomingData.unscheduled_reasons,
    loading,
    error,
    refetch: () => loadSchedule(targetDate),
    refetchUpcoming: () => {
      const today = todayKey();
      return loadSchedules(today, shiftDateKey(today, PLANNED_WINDOW_DAYS));
    },
    reschedule,
    generateSchedules,
    getScheduledDate,
  };
}
